import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FilterPanel from "../common/FilterPanel";
import FilterChips, { countActive } from "../common/FilterChips";
import { USER_PACK_FILTERS } from "../../config/filterConfigs";
import {
  faBox,
  faSpinner,
  faGift,
  faRotateLeft,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const PACK_TYPES = ["free", "single_unlock", "spark_bundle", "premium", "elite", "annual"];
const PACK_COLORS = {
  free:          "bg-gray-100 text-gray-600",
  single_unlock: "bg-blue-100 text-blue-700",
  spark_bundle:  "bg-cyan-100 text-cyan-700",
  premium:       "bg-purple-100 text-purple-700",
  elite:         "bg-yellow-100 text-yellow-700",
  annual:        "bg-green-100 text-green-700",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const SummaryCard = ({ label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color || "text-gray-800"}`}>{value}</p>
  </div>
);

const GrantModal = ({ userId, onSave, onClose }) => {
  const [form, setForm] = useState({ packType: "premium", durationDays: 90, note: "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.packType || !form.durationDays) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="font-semibold text-lg text-gray-800">Grant Pack</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500">User ID: <span className="font-mono">{userId}</span></p>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pack Type</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={form.packType}
              onChange={(e) => setForm(f => ({ ...f, packType: e.target.value }))}
            >
              {PACK_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Duration (days)</label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={form.durationDays}
              onChange={(e) => setForm(f => ({ ...f, durationDays: Number(e.target.value) }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Note (optional)</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Reason for grant"
              value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <FontAwesomeIcon icon={faSpinner} spin />}
            Grant Pack
          </button>
        </div>
      </div>
    </div>
  );
};

const UserPackManager = () => {
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expiringPacks, setExpiringPacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("all"); // 'all' | 'expiring'
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [grantModal, setGrantModal] = useState(null); // userId
  const [resetting, setResetting] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await func.get("mst-admin/user-packs/summary");
      if (res?.status === 200 && res.data?.success) setSummary(res.data);
    } catch {}
  }, []);

  const fetchPacks = useCallback(async (p = 1, f = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (f.packType) params.append("packType", f.packType);
      if (f.status) params.append("status", f.status);
      if (f.search) params.append("search", f.search);
      const res = await func.get(`mst-admin/user-packs?${params}`);
      if (res?.status === 200 && res.data?.success) {
        setPacks(res.data.packs || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to load packs");
    }
    setLoading(false);
  }, []);

  const fetchExpiring = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get("mst-admin/user-packs/expiring?days=7");
      if (res?.status === 200 && res.data?.success) setExpiringPacks(res.data.packs || []);
    } catch {
      toast.error("Failed to load expiring packs");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSummary();
    if (tab === "all") fetchPacks(page, filters);
    else fetchExpiring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, reload, fetchSummary, fetchPacks, fetchExpiring]);

  const handleGrant = async (form) => {
    try {
      const res = await func.post(`mst-admin/user-packs/${grantModal}/grant`, form);
      if (res?.status === 200 && res.data?.success) {
        toast.success(res.data.message);
        setGrantModal(null);
        fetchPacks(page, filters);
        fetchSummary();
      } else {
        toast.error(res?.data?.message || "Grant failed");
      }
    } catch {
      toast.error("Grant failed");
    }
  };

  const handleResetQuota = async (userId) => {
    if (!window.confirm("Reset all quota counters for this user?")) return;
    setResetting(userId);
    try {
      const res = await func.post(`mst-admin/user-packs/${userId}/reset-quota`, {});
      if (res?.status === 200 && res.data?.success) {
        toast.success("Quota reset successfully");
        fetchPacks(page, filters);
      } else {
        toast.error(res?.data?.message || "Reset failed");
      }
    } catch {
      toast.error("Reset failed");
    }
    setResetting(null);
  };

  const displayPacks = tab === "all" ? packs : expiringPacks;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FontAwesomeIcon icon={faBox} className="text-2xl text-purple-600" />
        <h1 className="text-2xl font-bold text-gray-800">User Pack Manager</h1>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(summary.breakdown || []).slice(0, 3).map((b) => (
            <SummaryCard key={b._id} label={b._id} value={b.count} />
          ))}
          <SummaryCard
            label="Expiring in 7 days"
            value={summary.expiringIn7Days || 0}
            color="text-red-600"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b">
        {["all", "expiring"].map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); setPage(1); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === t ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "all" ? "All Users" : "⚠️ Expiring Soon"}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        {tab === "all" && (
          <div className="p-4 border-b flex items-center gap-3">
            <p className="font-semibold text-gray-700">All Packs ({total})</p>
            <button onClick={() => setShowFilter(true)} className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition">
              Filters
              {countActive(USER_PACK_FILTERS, filters) > 0 && (<span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-purple-600 text-white rounded-full w-5 h-5">{countActive(USER_PACK_FILTERS, filters)}</span>)}
            </button>
            <FilterChips config={USER_PACK_FILTERS} filters={filters} onClearKeys={(keys) => { const n = { ...filters }; keys.forEach((k) => { n[k] = ""; }); setFilters(n); setPage(1); setReload((r) => !r); }} onClearAll={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />
          </div>
        )}

        <FilterPanel isOpen={showFilter} onClose={() => setShowFilter(false)} title="Filter User Packs" config={USER_PACK_FILTERS} filters={filters} onChange={setFilters} onApply={() => { setPage(1); setReload((r) => !r); }} onReset={() => { setFilters({}); setPage(1); setReload((r) => !r); }} />

        {loading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b text-left">
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Pack</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Unlocks</th>
                  <th className="px-4 py-3">Contacts</th>
                  <th className="px-4 py-3">Browse</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayPacks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-400">No packs found.</td>
                  </tr>
                ) : displayPacks.map((pack) => (
                  <tr key={pack._id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{pack.userId?.toString().slice(-8)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PACK_COLORS[pack.packType] || ""}`}>
                        {pack.packType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {tab === "expiring" ? (
                        <span className="text-red-600 font-medium">{fmtDate(pack.packExpiry)}</span>
                      ) : fmtDate(pack.packExpiry)}
                    </td>
                    <td className="px-4 py-3 text-xs">{pack.unlocksUsed}/{pack.unlockLimit}</td>
                    <td className="px-4 py-3 text-xs">{pack.contactsUsed}/{pack.contactLimit}</td>
                    <td className="px-4 py-3 text-xs">{pack.browseCountToday}/{pack.dailyBrowseLimit === -1 ? "∞" : pack.dailyBrowseLimit}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button
                          onClick={() => setGrantModal(pack.userId)}
                          className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faGift} />
                          Grant
                        </button>
                        <button
                          onClick={() => handleResetQuota(pack.userId)}
                          disabled={resetting === pack.userId}
                          className="text-xs text-orange-500 hover:text-orange-700 flex items-center gap-1 disabled:opacity-50"
                        >
                          <FontAwesomeIcon icon={resetting === pack.userId ? faSpinner : faRotateLeft} spin={resetting === pack.userId} />
                          Reset
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "all" && total > 20 && (
          <div className="flex justify-between items-center p-4 border-t text-sm text-gray-500">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Prev</button>
            <span>Page {page} of {Math.ceil(total / 20)}</span>
            <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {grantModal && (
        <GrantModal userId={grantModal} onSave={handleGrant} onClose={() => setGrantModal(null)} />
      )}
    </div>
  );
};

export default UserPackManager;
